import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import ContactFooter from '@/pages/home/components/ContactFooter';
import { useTranslation } from 'react-i18next';

const CATERING_TRUCKS = [
  {
    name: 'La Jolla Commissary',
    address: '166 E. La Jolla St.',
    cityStateZip: 'Placentia, CA 92870',
  },
  {
    name: 'International Catering',
    address: '16221 Construction Cir W',
    cityStateZip: 'Irvine, CA 92606',
  },
  {
    name: 'Santa Ana Catering Inc',
    address: '2421 S Broadway',
    cityStateZip: 'Santa Ana, CA 92707',
  },
];

const PREPACKAGED_TRUCKS = [
  {
    name: 'OC Food Truck Company',
    address: '2419 S Broadway',
    cityStateZip: 'Santa Ana, CA 92707',
  },
  {
    name: 'Carousel Foods',
    address: '2520 S Birch St.',
    cityStateZip: 'Santa Ana, CA 92707',
  },
  {
    name: 'Fast Dash Commissary',
    address: '3091 E Coronado',
    cityStateZip: 'Anaheim, CA 92806',
  },
];

function CommissaryCard({ name, address, cityStateZip }: {
  name: string;
  address: string;
  cityStateZip: string;
}) {
  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200 hover:border-teal-300 transition-colors duration-200">
      <h3 className="text-lg font-bold text-gray-900 mb-3">{name}</h3>
      <div className="space-y-1 text-gray-600 text-sm leading-relaxed">
        <p>{address}</p>
        <p>{cityStateZip}</p>
      </div>
    </div>
  );
}

export default function Commissary() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <main className="overflow-x-hidden">
      <Navbar />
      {/* Spacer for fixed navbar */}
      <div className="h-20" />

      {/* Page Header */}
      <section className="bg-teal-50 py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-teal-400 rounded-full" />
            <span className="text-gray-500 text-sm font-medium tracking-wide uppercase">{t('commissary.tag')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            {t('commissary.title1')} <span className="text-teal-400">{t('commissary.title2')}</span>
          </h1>
          <p className="text-gray-500 text-lg mt-4 max-w-2xl mx-auto">
            {t('commissary.subtitle')}
          </p>
        </div>
      </section>

      {/* Catering Trucks Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-lg">
              <i className="ri-restaurant-line text-teal-500 text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{t('commissary.cateringTrucks')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATERING_TRUCKS.map((item) => (
              <CommissaryCard key={item.name} {...item} />
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="h-px bg-stone-200" />
      </div>

      {/* Prepackaged Trucks Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-lg">
              <i className="ri-store-2-line text-teal-500 text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{t('commissary.prepackagedTrucks')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PREPACKAGED_TRUCKS.map((item) => (
              <CommissaryCard key={item.name} {...item} />
            ))}
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-14 px-6 bg-teal-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-4">
            <i className="ri-information-line text-teal-500 text-xl" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('commissary.needHelp')}</h3>
          <p className="text-gray-500 text-base max-w-xl mx-auto mb-6">
            {t('commissary.needHelpText')}
          </p>
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.location.hash = '#consultation';
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-gray-900 text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('commissary.bookSession')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      <ContactFooter />
    </main>
  );
}